/**
 * Interactive "how the site works" guide — approved design v3 (2026-10-19).
 * Rendered inside QuickGuideModal (Dialog on ≥640px, Drawer on phones) and
 * the guest side-tab QuickGuideFAB; it only opens when the user asks.
 *
 * Four journeys: buy, sell a personal item, open a virtual store, wholesaler.
 * The store and wholesaler journeys carry the detailed pitch, steps and (for
 * stores) a profit calculator whose rate is the LIVE platform commission
 * (GET /api/settings/dropshipping → platformCommissionPercent). Copy lives in
 * ./guideContent.ts; buttons open the real pages for the viewer's role.
 */

import { useMemo, useState, type ElementType } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { X, Check, ChevronLeft, ChevronRight, Calculator, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/use-auth";
import { isStoreOwnerRole, isWholesaleMerchantOnly } from "@/lib/userRoles";
import { cn } from "@/lib/utils";
import { calcMinResalePrice } from "@shared/constants";
import { GUIDE_COPY, type GuideRole } from "./guideContent";

const ROLES: { role: GuideRole; emoji: string; tint: string }[] = [
  { role: "buy", emoji: "🛍️", tint: "bg-blue-50 dark:bg-blue-500/15" },
  { role: "sell", emoji: "📸", tint: "bg-amber-50 dark:bg-amber-500/15" },
  { role: "store", emoji: "🏪", tint: "bg-emerald-50 dark:bg-emerald-500/15" },
  { role: "trade", emoji: "📦", tint: "bg-violet-50 dark:bg-violet-500/15" },
];

/** Fallback when the setting can't be read — the server's own default. */
const DEFAULT_COMMISSION_PERCENT = 20;

const LOCALES: Record<string, string> = { ar: "ar-IQ", en: "en-US", tr: "tr-TR", zh: "zh-CN" };

function fill(template: string, values: Record<string, string>) {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m);
}

export function HowItWorksGuide({
  onNavigate,
  onClose,
  TitleTag = "h2",
  DescriptionTag = "p",
}: {
  /** Called before following a link, so the dialog closes. */
  onNavigate: () => void;
  onClose: () => void;
  /** Dialog/Drawer title + description primitives (accessible name). */
  TitleTag?: ElementType;
  DescriptionTag?: ElementType;
}) {
  const { language, isRTL } = useLanguage();
  const { user } = useAuth();
  const copy = GUIDE_COPY[language] ?? GUIDE_COPY.ar!;
  const locale = LOCALES[language] ?? "ar-IQ";
  const nf = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }), [locale]);

  const { data: settings } = useQuery<{
    platformCommissionPercent?: number;
    lowTierPercent?: number;
    highTierPercent?: number;
    thresholdIQD?: number;
  }>({
    queryKey: ["/api/settings/dropshipping"],
    staleTime: 5 * 60_000,
  });
  const ratePercent = (() => {
    const v = Number(settings?.platformCommissionPercent);
    return Number.isFinite(v) && v >= 0 && v <= 100 ? v : DEFAULT_COMMISSION_PERCENT;
  })();
  const rate = ratePercent / 100;
  const rateText = nf.format(ratePercent);
  const money = (n: number) => `${nf.format(n)} ${copy.currency}`;

  const [role, setRole] = useState<GuideRole>("store");
  const [step, setStep] = useState(0);
  const [cost, setCost] = useState("20000");
  const [price, setPrice] = useState("30000");

  const g = copy.roles[role];
  const steps = g.steps;
  const current = steps[Math.min(step, steps.length - 1)]!;
  // Worked example in the pricing step: 20,000 → 30,000, margin 10,000.
  const exampleFee = Math.round(10_000 * rate);
  const fillValues = { rate: rateText, fee: money(exampleFee), net: money(10_000 - exampleFee) };

  const costN = Math.max(0, Number(cost) || 0);
  const priceN = Math.max(0, Number(price) || 0);
  const margin = Math.max(0, priceN - costN);
  const fee = Math.round(margin * rate);
  const net = margin - fee;
  // The same minimum the server enforces when a store saves a resale price.
  const { minPrice } = calcMinResalePrice(costN, "IQD", {
    lowTierPercent: settings?.lowTierPercent,
    highTierPercent: settings?.highTierPercent,
    thresholdIQD: settings?.thresholdIQD,
  });
  const belowCost = priceN <= costN;
  const belowMin = !belowCost && costN > 0 && priceN < minPrice;
  const warn = belowCost || belowMin;

  // Buttons open the real page for the viewer's role.
  const ctas: { href: string; label: string; primary?: boolean }[] = (() => {
    switch (role) {
      case "buy":
        return [{ href: "/search", label: copy.cta.buy, primary: true }];
      case "sell":
        // Store and wholesaler accounts don't post personal ads.
        if (isStoreOwnerRole(user)) {
          return [
            { href: "/create-listing?virtualStoreOwn=1", label: copy.cta.sellStoreOwn, primary: true },
            { href: "/register/customer", label: copy.cta.separateCustomer },
          ];
        }
        if (isWholesaleMerchantOnly(user)) {
          return [
            { href: "/create-listing", label: copy.cta.sellWholesale, primary: true },
            { href: "/register/customer", label: copy.cta.separateCustomer },
          ];
        }
        return [{ href: "/create-listing", label: copy.cta.sell, primary: true }];
      case "store":
        if (isStoreOwnerRole(user)) return [{ href: "/my-reseller-inventory", label: copy.cta.storeManage, primary: true }];
        if (isWholesaleMerchantOnly(user)) {
          return [
            { href: "/register/customer", label: copy.cta.storeSeparate, primary: true },
            { href: "/work-with-us-info", label: copy.cta.storeMore },
          ];
        }
        return [
          { href: "/work-with-us", label: copy.cta.storeOpen, primary: true },
          { href: "/work-with-us-info", label: copy.cta.storeMore },
        ];
      case "trade":
        return isWholesaleMerchantOnly(user)
          ? [{ href: "/supplier-dashboard", label: copy.cta.tradeDashboard, primary: true }]
          : [{ href: "/register/wholesale", label: copy.cta.tradeRegister, primary: true }];
    }
  })();

  // Friday ads are personal ads: not for store or wholesaler accounts.
  const canPostPersonalAds = !isStoreOwnerRole(user) && !isWholesaleMerchantOnly(user);

  const selectRole = (r: GuideRole) => {
    setRole(r);
    setStep(0);
  };

  const BackIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="text-start" data-testid="how-it-works-guide">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#09275f] to-[#14519c] text-white px-4 py-4 sm:px-6 sm:py-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-28 -end-12 w-64 h-64 rounded-full border border-white/10 shadow-[0_0_0_45px_rgba(255,255,255,0.035),0_0_0_100px_rgba(255,255,255,0.02)]"
        />
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="inline-block rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[10px] sm:text-[11px]">
              {copy.eyebrow}
            </span>
            <TitleTag className="mt-2 text-lg sm:text-[22px] font-extrabold leading-snug text-white">{copy.title}</TitleTag>
            <DescriptionTag className="mt-1 text-[11.5px] sm:text-xs leading-relaxed text-blue-100">{copy.subtitle}</DescriptionTag>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={copy.close}
            className="shrink-0 grid place-items-center w-8 h-8 rounded-lg border border-white/40 bg-white/10 text-white hover:bg-white/20"
            data-testid="button-guide-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-5 space-y-3">
        {/* Journeys */}
        <div role="group" aria-label={copy.rolesLabel} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {ROLES.map(({ role: r, emoji, tint }) => {
            const active = r === role;
            return (
              <button
                key={r}
                type="button"
                aria-pressed={active}
                onClick={() => selectRole(r)}
                className={cn(
                  "flex sm:flex-col items-center sm:items-start gap-2 rounded-xl border p-2 sm:p-2.5 text-start transition-colors min-w-0",
                  active
                    ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600/20 dark:bg-blue-500/15 dark:border-blue-400"
                    : "bg-card hover:bg-muted/50",
                )}
                data-testid={`guide-role-${r}`}
              >
                <span className={cn("grid place-items-center w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-lg text-lg sm:text-xl", tint)} aria-hidden="true">
                  {emoji}
                </span>
                <span className="min-w-0">
                  <span className={cn("block text-[11px] sm:text-xs font-bold leading-tight", active && "text-blue-800 dark:text-blue-200")}>
                    {copy.roles[r].tab}
                  </span>
                  <span className="hidden sm:block mt-0.5 text-[10px] leading-snug text-muted-foreground">{copy.roles[r].tabHint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Pitch */}
        <section className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/80 to-white p-3 sm:p-4 dark:border-blue-900/50 dark:from-blue-950/30 dark:to-transparent">
          <h3 className="text-[15px] sm:text-[17px] font-extrabold text-blue-900 dark:text-blue-200">{g.title}</h3>
          <p className="mt-1 text-[11.5px] sm:text-[12.5px] leading-7 text-slate-600 dark:text-slate-300">{g.intro}</p>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {g.badges.map((b) => (
              <div key={b} className="rounded-lg border border-blue-100 bg-white px-1.5 py-2 text-center text-[10px] sm:text-[11px] font-bold leading-snug text-blue-800 dark:border-blue-900/50 dark:bg-card dark:text-blue-200">
                ✓ {b}
              </div>
            ))}
          </div>
        </section>

        {/* Trade: how goods reach customers */}
        {g.flow && (
          <section className="rounded-xl border bg-card p-3" data-testid="guide-trade-flow">
            <h4 className="text-xs font-bold text-blue-900 dark:text-blue-200 mb-2">{g.flowTitle}</h4>
            <div className="flex items-stretch gap-1.5">
              {g.flow.map((label, i) => (
                <div key={label} className="flex items-center gap-1.5 flex-1 min-w-0">
                  <div className={cn(
                    "flex-1 min-w-0 rounded-lg px-2 py-2 text-center text-[10.5px] sm:text-xs font-bold leading-snug",
                    i === 0 ? "bg-violet-50 text-violet-800 dark:bg-violet-500/15 dark:text-violet-200"
                      : i === 1 ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200"
                        : "bg-blue-50 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
                  )}>
                    {label}
                  </div>
                  {i < 2 && <ArrowLeft className="w-4 h-4 shrink-0 text-muted-foreground ltr:rotate-180" aria-hidden="true" />}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Store / trade: the two strongest points */}
        {g.highlights && (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="guide-highlights">
            {g.highlights.map((h, i) => (
              <div
                key={h.title}
                className={cn(
                  "rounded-xl border p-3",
                  i === 0
                    ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20"
                    : "border-blue-200 bg-blue-50/60 dark:border-blue-900/60 dark:bg-blue-950/20",
                )}
              >
                <p className={cn("text-[12.5px] font-extrabold", i === 0 ? "text-emerald-800 dark:text-emerald-200" : "text-blue-900 dark:text-blue-200")}>
                  {fill(h.title, fillValues)}
                </p>
                <p className="mt-1 text-[11.5px] leading-6 text-slate-600 dark:text-slate-300">{fill(h.text, fillValues)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Steps */}
        <div className="rounded-xl border overflow-hidden sm:grid sm:grid-cols-[190px_minmax(0,1fr)]">
          <nav
            aria-label={copy.stepsLabel}
            className="flex sm:flex-col gap-1 overflow-x-auto bg-slate-50 p-1.5 sm:p-2.5 border-b sm:border-b-0 sm:border-e dark:bg-muted/30 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {steps.map((s, i) => {
              const active = i === step;
              return (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => setStep(i)}
                  aria-current={active ? "step" : undefined}
                  className={cn(
                    "flex shrink-0 items-center gap-2 rounded-lg border px-2 py-1.5 sm:py-2 text-start text-[10.5px] sm:text-[11px] leading-snug whitespace-nowrap sm:whitespace-normal transition-colors",
                    active ? "border-blue-200 bg-white font-bold text-blue-800 dark:bg-card dark:text-blue-200" : "border-transparent text-slate-500 hover:bg-white/70 dark:text-slate-400 dark:hover:bg-card/60",
                  )}
                  data-testid={`guide-step-${i}`}
                >
                  <span className={cn(
                    "grid place-items-center w-5 h-5 sm:w-6 sm:h-6 shrink-0 rounded-md border text-[10px] sm:text-[11px]",
                    active ? "border-blue-700 bg-blue-700 text-white" : "border-slate-200 bg-white text-blue-700 dark:bg-card dark:border-slate-700",
                  )}>
                    {nf.format(i + 1)}
                  </span>
                  <span>{s.title}</span>
                </button>
              );
            })}
          </nav>
          <section aria-live="polite" className="flex flex-col justify-between p-3 sm:p-4 min-h-[190px]">
            <div>
              <span className="text-[10px] font-bold text-blue-500">
                {fill(copy.stepCounter, { n: nf.format(step + 1), total: nf.format(steps.length) })}
              </span>
              <h4 className="mt-1 text-[14px] sm:text-base font-extrabold text-blue-900 dark:text-blue-200">{current.title}</h4>
              <p className="mt-1 text-xs leading-7 text-slate-600 dark:text-slate-300">{fill(current.text, fillValues)}</p>
              <p className="mt-2.5 rounded-lg border-s-[3px] border-amber-500 bg-amber-50 px-2.5 py-2 text-[11px] leading-6 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                {fill(current.note, fillValues)}
              </p>
            </div>
            <div className="mt-3 flex justify-end gap-1.5">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="inline-flex items-center gap-1 rounded-lg border bg-card px-3 py-1.5 text-[11px] text-blue-900 disabled:opacity-40 dark:text-blue-200"
                data-testid="button-guide-prev"
              >
                <BackIcon className="w-3.5 h-3.5" />
                {copy.prev}
              </button>
              <button
                type="button"
                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                disabled={step === steps.length - 1}
                className="inline-flex items-center gap-1 rounded-lg border bg-card px-3 py-1.5 text-[11px] text-blue-900 disabled:opacity-40 dark:text-blue-200"
                data-testid="button-guide-next"
              >
                {step === steps.length - 1 ? copy.done : copy.next}
                {step < steps.length - 1 && <NextIcon className="w-3.5 h-3.5" />}
              </button>
            </div>
          </section>
        </div>

        {/* Store: profit calculator (live commission rate) */}
        {role === "store" && (
          <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 sm:p-4 dark:border-emerald-900/60 dark:bg-emerald-950/20" data-testid="guide-calculator">
            <h4 className="flex items-center gap-1.5 text-sm font-extrabold text-emerald-800 dark:text-emerald-200">
              <Calculator className="w-4 h-4" />
              {copy.calc.title}
            </h4>
            <p className="mt-1 text-[11px] leading-6 text-emerald-900/70 dark:text-emerald-100/70">{copy.calc.sub}</p>
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-[11px] font-bold text-emerald-900 dark:text-emerald-100">
                {copy.calc.cost}
                <input
                  type="number"
                  min={0}
                  step={1000}
                  inputMode="numeric"
                  dir="ltr"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  className="w-full rounded-lg border border-emerald-200 bg-white px-2.5 py-2 text-[13px] text-start font-normal text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:bg-card dark:border-emerald-900"
                  data-testid="input-guide-cost"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold text-emerald-900 dark:text-emerald-100">
                {copy.calc.price}
                <input
                  type="number"
                  min={0}
                  step={1000}
                  inputMode="numeric"
                  dir="ltr"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full rounded-lg border border-emerald-200 bg-white px-2.5 py-2 text-[13px] text-start font-normal text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:bg-card dark:border-emerald-900"
                  data-testid="input-guide-price"
                />
              </label>
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-1.5">
              {[
                { label: copy.calc.margin, value: money(margin), testId: "guide-calc-margin", tone: warn ? "text-red-700" : "text-emerald-700 dark:text-emerald-300" },
                { label: fill(copy.calc.fee, { rate: rateText }), value: money(fee), testId: "guide-calc-fee", tone: "text-amber-700 dark:text-amber-300" },
                { label: copy.calc.net, value: money(net), testId: "guide-calc-net", tone: warn ? "text-red-700" : "text-emerald-700 dark:text-emerald-300" },
              ].map((r) => (
                <div key={r.testId} className="rounded-lg border border-emerald-100 bg-white px-1.5 py-2 text-center dark:bg-card dark:border-emerald-900">
                  <span className="block text-[10px] text-muted-foreground">{r.label}</span>
                  <b className={cn("mt-0.5 block text-[12px] sm:text-[15px] font-extrabold", r.tone)} data-testid={r.testId}>{r.value}</b>
                </div>
              ))}
            </div>
            <p
              className={cn("mt-2 text-[10px] leading-5", warn ? "font-bold text-red-700 dark:text-red-300" : "text-emerald-900/70 dark:text-emerald-100/70")}
              data-testid="guide-calc-disclaimer"
            >
              {belowCost
                ? copy.calc.belowCost
                : belowMin
                  ? fill(copy.calc.belowMin, { min: money(minPrice) })
                  : fill(copy.calc.disclaimer, { rate: rateText })}
            </p>
          </section>
        )}

        {/* Callout */}
        <section className="rounded-xl border bg-slate-50/70 p-3 dark:bg-muted/20">
          <h4 className="text-xs font-bold text-blue-900 dark:text-blue-200">{g.calloutTitle}</h4>
          <p className="mt-1 text-[11px] leading-6 text-slate-600 dark:text-slate-300">{g.calloutText}</p>
        </section>

        {/* Real pages for this journey */}
        <div className="flex flex-wrap items-center gap-2">
          {ctas.map((c) => (
            <Link
              key={c.href}
              href={c.href}
              onClick={onNavigate}
              className={cn(
                c.primary
                  ? "inline-flex items-center gap-1.5 rounded-xl gradient-cta px-4 py-2.5 text-xs font-bold text-white shadow-md shadow-orange-500/30"
                  : "inline-flex items-center gap-1 text-xs font-bold text-blue-800 hover:underline dark:text-blue-300",
              )}
              data-testid={`guide-cta-${role}${c.primary ? "" : "-secondary"}`}
            >
              {c.label}
              <ArrowLeft className="w-3.5 h-3.5 ltr:rotate-180" aria-hidden="true" />
            </Link>
          ))}
        </div>

        {/* Friday market */}
        <details className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 dark:border-amber-900/50 dark:bg-amber-950/20">
          <summary className="cursor-pointer py-2.5 text-xs font-bold text-amber-900 dark:text-amber-200">▦ {copy.friday.summary}</summary>
          <p className="text-[11px] leading-6 text-amber-900/80 dark:text-amber-100/80">{copy.friday.text}</p>
          {canPostPersonalAds ? (
            <Link
              href="/create-listing?source=friday-market"
              onClick={onNavigate}
              className="mb-3 mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 hover:underline dark:text-amber-300"
              data-testid="guide-friday-cta"
            >
              <Check className="w-3.5 h-3.5" />
              {copy.friday.cta}
            </Link>
          ) : (
            <span className="block h-3" aria-hidden="true" />
          )}
        </details>
      </div>
    </div>
  );
}
